/**
 * Kunci idempotensi untuk mutasi absensi.
 *
 * Inti persoalannya bukan "membuat UUID" — melainkan **kapan kunci itu boleh berubah.**
 *
 * Kunci baru pada setiap klik akan menghasilkan dua event untuk satu niat: pengguna lapangan
 * menekan tombol dua kali karena jaringan lambat, dan server melihat dua permintaan berbeda.
 * Sebaliknya, kunci yang tidak pernah berubah membuat check-out esok hari dianggap pengulangan
 * check-out kemarin dan mengembalikan hasil lama tanpa mencatat apa pun.
 *
 * Karena itu kunci di sini terikat pada SATU NIAT: dibuat saat niat mulai, dipertahankan selama
 * percobaan ulang niat yang sama, dan hanya diputar setelah niat itu benar-benar selesai.
 */

export type KeyFactory = () => string;

const defaultFactory: KeyFactory = () =>
  // `crypto.randomUUID` tersedia di seluruh browser target dan pada Node 19+ untuk pengujian.
  globalThis.crypto.randomUUID();

/**
 * Pemegang kunci untuk satu niat.
 *
 * ```
 * const holder = createIdempotencyKeyHolder();
 * holder.current()   // kunci A — percobaan pertama
 * holder.current()   // kunci A — percobaan ulang, TETAP sama
 * holder.rotate()    // niat selesai
 * holder.current()   // kunci B — niat berikutnya
 * ```
 */
export interface IdempotencyKeyHolder {
  /** Kunci untuk niat yang sedang berjalan. Stabil sampai `rotate()`. */
  current(): string;
  /** Menandai niat selesai; panggilan `current()` berikutnya memulai kunci baru. */
  rotate(): void;
}

export function createIdempotencyKeyHolder(
  factory: KeyFactory = defaultFactory,
): IdempotencyKeyHolder {
  let key: string | null = null;
  return {
    current(): string {
      key ??= factory();
      return key;
    },
    rotate(): void {
      key = null;
    },
  };
}

/** Header mutasi. Dipisahkan agar tidak ada layar yang lupa menyertakannya. */
export function idempotencyHeaders(key: string): Record<string, string> {
  return { 'idempotency-key': key };
}
